import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';

import { REDACTED_FIELD } from '../../../config/logrocket';
import { formatActivityTimestamp, isFromToday } from './activityRecency';
import {
  getDashboardPanelTokens,
  rem,
  TILE_SUBTITLE_GAP,
} from './dashboardPanelTokens';
import { TileHeading } from './TileHeading';

export interface ActivityEvent {
  readonly id: string;
  readonly eventSummary: string;
  /** Epoch milliseconds, from the activity service's `modified` field. */
  readonly modified?: number;
}

interface RecentActivityPanelProps {
  readonly events: readonly ActivityEvent[];
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  /** Destination for the header link; omitted hides the link. */
  readonly viewLogHref?: string;
  readonly title?: string;
}

/**
 * The dashboard's recent-activity list. Each row is dotted by recency --
 * today's events read as live, everything older recedes -- which stays correct
 * as the activity service's event vocabulary grows, unlike a per-event-type
 * colour map.
 */
export function RecentActivityPanel({
  events,
  isLoading = false,
  isError = false,
  viewLogHref,
  title = 'Recent activity',
}: Readonly<RecentActivityPanelProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    // The dashboard wraps some tiles in a flex Paper, where a plain block
    // child collapses to its content width instead of filling the card.
    <Box data-qa="RECENT_ACTIVITY_PANEL" sx={{ width: '100%', minWidth: 0 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="1rem"
        pb={TILE_SUBTITLE_GAP}
      >
        <TileHeading>{title}</TileHeading>
        {viewLogHref ? (
          <Typography
            component={Link}
            to={viewLogHref}
            sx={{
              flexShrink: 0,
              fontSize: rem(13.5),
              fontWeight: 600,
              color: tokens.brand,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View log
          </Typography>
        ) : null}
      </Box>

      <PanelBody events={events} isLoading={isLoading} isError={isError} />
    </Box>
  );
}

function PanelBody({
  events,
  isLoading,
  isError,
}: Readonly<{
  events: readonly ActivityEvent[];
  isLoading: boolean;
  isError: boolean;
}>) {
  if (isLoading) {
    return <ActivitySkeleton />;
  }

  if (isError) {
    return (
      <EmptyMessage text="Recent activity is unavailable right now. Please try again in a few minutes." />
    );
  }

  if (!events.length) {
    return <EmptyMessage text="No recent activity." />;
  }

  return (
    <Box
      component="ul"
      sx={{ listStyle: 'none', margin: 0, padding: 0, marginTop: '0.25rem' }}
    >
      {events.map((event) => (
        <ActivityRow key={event.id} event={event} />
      ))}
    </Box>
  );
}

function ActivityRow({ event }: Readonly<{ event: ActivityEvent }>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);
  const recent = isFromToday(event.modified);
  const timestamp = formatActivityTimestamp(event.modified);

  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        columnGap: rem(12),
        padding: `${rem(9)} 0`,
      }}
    >
      <Box
        aria-hidden="true"
        data-qa={recent ? 'ACTIVITY_DOT_TODAY' : 'ACTIVITY_DOT_OLDER'}
        sx={{
          width: rem(8),
          height: rem(8),
          flexShrink: 0,
          borderRadius: '50%',
          marginTop: rem(6),
          backgroundColor: recent ? tokens.brand : tokens.inkFaint,
        }}
      />
      <Box minWidth={0}>
        <Typography
          sx={{
            fontSize: rem(13),
            lineHeight: 1.45,
            color: tokens.inkBody,
            // An API-key name, email or URL is one token with no break point,
            // and runs past the card once Ask Moo narrows the column.
            // `anywhere` breaks it only when it would overflow, and unlike
            // `break-word` it also lowers the min-content width the grid reads.
            overflowWrap: 'anywhere',
          }}
        >
          {/* The class stays on the summary alone. Wrapping the timestamp in it
              too would redact a value that is not the reader's data. */}
          <Box component="span" className={REDACTED_FIELD}>
            {event.eventSummary}
          </Box>
          {/*
            Floated, and declared AFTER the summary: a float lands on the line
            box it occurs in, so from here it settles on the last wrapped line
            rather than reserving space on the first. That buys back the line
            the timestamp used to occupy on its own -- five rows of it in a tile
            this size.
          */}
          {timestamp ? (
            <Box
              component="span"
              sx={{
                float: 'right',
                marginLeft: rem(10),
                fontSize: rem(11.5),
                color: tokens.inkFaint,
                whiteSpace: 'nowrap',
              }}
            >
              {timestamp}
            </Box>
          ) : null}
        </Typography>
      </Box>
    </Box>
  );
}

function EmptyMessage({ text }: Readonly<{ text: string }>) {
  return (
    <Typography sx={{ paddingY: '1rem', color: 'text.secondary' }}>
      {text}
    </Typography>
  );
}

function ActivitySkeleton() {
  return (
    <Box data-qa="RECENT_ACTIVITY_LOADING" mt="0.25rem">
      {Array.from({ length: 5 }).map((_unused, index) => (
        <Box
          key={`activity-skeleton-${index}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            columnGap: '0.833rem',
            padding: '0.667rem 0',
          }}
        >
          <Skeleton
            variant="circular"
            sx={{ width: rem(8), height: rem(8), marginTop: rem(6) }}
          />
          <Box>
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="35%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default RecentActivityPanel;
